import { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, FileText, CreditCard, Trash2, Edit, Filter, X, DollarSign, Check, AlertCircle, Calendar, Save, Eye, Upload, FileDown, RefreshCw, Download, TrendingUp, XCircle, Search } from 'lucide-react';
import { searchMatch, formatDateDisplay } from '@/utils/formatters';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const FORMAS_PAGAMENTO = ['CRÉDITO', 'DÉBITO', 'PIX', 'ESPÉCIE'];
const BANDEIRAS = ['VISA', 'MASTERCARD', 'ELO', 'AMEX', 'PIX'];

const getMesesReferencia = () => {
  const meses = [];
  const nomeMeses = ['JANEIRO', 'FEVEREIRO', 'MARÇO', 'ABRIL', 'MAIO', 'JUNHO', 
                     'JULHO', 'AGOSTO', 'SETEMBRO', 'OUTUBRO', 'NOVEMBRO', 'DEZEMBRO'];
  const hoje = new Date();
  // Gera meses de 18 meses atrás até 6 meses à frente (permite cadastrar NF-es do ano anterior)
  for (let i = -18; i <= 6; i++) {
    const data = new Date(hoje.getFullYear(), hoje.getMonth() + i, 1);
    const mes = nomeMeses[data.getMonth()];
    const ano = data.getFullYear().toString().slice(-2);
    meses.push(`${mes}/${ano}`);
  }
  return meses;
};

// Função para ordenar meses cronologicamente (mais recente primeiro)
const ordenarMesesCronologicamente = (meses) => {
  const nomeMesesMap = {
    'JANEIRO': 1, 'FEVEREIRO': 2, 'MARÇO': 3, 'ABRIL': 4, 'MAIO': 5, 'JUNHO': 6,
    'JULHO': 7, 'AGOSTO': 8, 'SETEMBRO': 9, 'OUTUBRO': 10, 'NOVEMBRO': 11, 'DEZEMBRO': 12
  };
  
  return [...meses].sort((a, b) => {
    const [mesA, anoA] = a.split('/');
    const [mesB, anoB] = b.split('/');
    const anoFullA = parseInt(anoA) < 50 ? 2000 + parseInt(anoA) : 1900 + parseInt(anoA);
    const anoFullB = parseInt(anoB) < 50 ? 2000 + parseInt(anoB) : 1900 + parseInt(anoB);
    
    if (anoFullB !== anoFullA) return anoFullB - anoFullA; // Ano mais recente primeiro
    return nomeMesesMap[mesB] - nomeMesesMap[mesA]; // Mês mais recente primeiro
  });
};

export default function NFe() {
  const [nfes, setNfes] = useState([]);
  const [stats, setStats] = useState(null);
  const [faturamentoMensal, setFaturamentoMensal] = useState([]);
  const [filterFaturamento, setFilterFaturamento] = useState('todas'); // 'todas', 'faturadas', 'nao_faturadas'
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNfe, setEditingNfe] = useState(null);
  const [filterMes, setFilterMes] = useState('');
  const [filterForma, setFilterForma] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15); // NF-es por página
  
  // Modal de faturamento
  const [faturamentoDialogOpen, setFaturamentoDialogOpen] = useState(false);
  const [nfeParaFaturar, setNfeParaFaturar] = useState(null);
  const [faturamentoData, setFaturamentoData] = useState({
    mes_referencia: '',
    nfe_numero: ''
  });
  const [nfePdfFile, setNfePdfFile] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  
  // Sincronização com Omie
  const [sincronizando, setSincronizando] = useState(false);
  const [verificandoCanceladas, setVerificandoCanceladas] = useState(false);
  
  // Modal de visualização detalhada
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingNfe, setViewingNfe] = useState(null);
  const [orderPayments, setOrderPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  
  // Estados para clientes
  const [customers, setCustomers] = useState([]);
  const [clienteSearch, setClienteSearch] = useState('');
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const [clienteMode, setClienteMode] = useState('existente');
  const [novoCliente, setNovoCliente] = useState({ nome: '', telefone: '', cpf: '', tipo: 'PF' });
  
  const [formData, setFormData] = useState({
    data: new Date().toISOString().split('T')[0],
    nome: '',
    mes_referencia: getMesesReferencia()[3],
    faturado: '',
    nfe_numero: '',
    quant_nfe: '',
    observacao: '',
    status_faturamento: 'nao_faturada',
    cpf_cnpj: '',
    pagamentos: []
  });
  
  // Estado para formulário de pagamento individual
  const [pagamentoAtual, setPagamentoAtual] = useState({
    forma: 'CRÉDITO',
    bandeira: 'VISA',
    valor: '',
    quantidade: '1',
    nsu: ''
  });

  useEffect(() => {
    fetchNfes();
    fetchCustomers();
  }, [filterMes, filterForma, filterStatus]);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/customers`, { params: { all_data: true } });
      const data = Array.isArray(res.data) ? res.data : (res.data.customers || []);
      setCustomers(data);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  // Memoizar lista filtrada de clientes para evitar re-renders
  const filteredCustomers = useMemo(() => {
    if (!clienteSearch) return [];
    return customers.filter(c => searchMatch(c.nome, clienteSearch)).slice(0, 8);
  }, [customers, clienteSearch]);

  // Filtrar e ordenar NF-es por status de faturamento (abas)
  const filteredNfes = useMemo(() => {
    let filtered = nfes;
    
    // Filtrar por status
    if (filterFaturamento === 'faturadas') {
      filtered = nfes.filter(n => n.status_faturamento === 'faturada');
    } else if (filterFaturamento === 'nao_faturadas') {
      filtered = nfes.filter(n => n.status_faturamento !== 'faturada');
    }
    
    // Filtrar por busca (nome do cliente ou número da NF-e)
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.trim();
      filtered = filtered.filter(n =>
        searchMatch(n.nome || '', term) ||
        String(n.nfe_numero || '').includes(term) ||
        String(n.numero_nfe || '').includes(term) ||
        String(n.numero_fatura || '').includes(term)
      );
    }
    
    // Ordenar
    if (filterFaturamento === 'faturadas') {
      // Faturadas: ordenar pelo número da NF-e (maior para menor)
      return [...filtered].sort((a, b) => {
        const numA = parseInt(a.nfe_numero) || 0;
        const numB = parseInt(b.nfe_numero) || 0;
        return numB - numA;
      });
    } else {
      // Todas e Não Faturadas: ordenar por data (mais nova para mais antiga)
      return [...filtered].sort((a, b) => {
        const dateA = new Date(a.data);
        const dateB = new Date(b.data);
        return dateB - dateA;
      });
    }
  }, [nfes, filterFaturamento, searchTerm]);

  // Calcular paginação
  const totalPages = Math.ceil(filteredNfes.length / itemsPerPage);
  const paginatedNfes = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredNfes.slice(start, start + itemsPerPage);
  }, [filteredNfes, currentPage, itemsPerPage]);

  // Reset página quando muda filtro
  useEffect(() => {
    setCurrentPage(1);
  }, [filterFaturamento, filterMes, filterForma, filterStatus, searchTerm]);

  // Calcular faturamento anual (por ano)
  const faturamentoAnual = useMemo(() => {
    const porAno = {};
    nfes.filter(n => n.status_faturamento === 'faturada').forEach(nfe => {
      const mesRef = nfe.mes_referencia || '';
      const match = mesRef.match(/\/(\d{2,4})$/);
      if (match) {
        let ano = match[1];
        if (ano.length === 2) ano = `20${ano}`;
        if (!porAno[ano]) porAno[ano] = { total: 0, quantidade: 0 };
        porAno[ano].total += nfe.valor || 0;
        porAno[ano].quantidade += 1;
      }
    });
    return porAno;
  }, [nfes]);

  const fetchNfes = async () => {
    try {
      let url = `${API}/nfe`;
      const params = new URLSearchParams();
      if (filterMes) params.append('mes_referencia', filterMes);
      if (filterForma) params.append('forma', filterForma);
      if (filterStatus) params.append('status_faturamento', filterStatus);
      if (params.toString()) url += `?${params.toString()}`;
      
      const [nfeRes, statsRes, fatMensalRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/nfe/stats${filterMes ? `?mes_referencia=${filterMes}` : ''}`),
        axios.get(`${API}/nfe/faturamento-por-mes?limite_meses=12`)
      ]);
      
      setNfes(nfeRes.data);
      setStats(statsRes.data);
      setFaturamentoMensal(fatMensalRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar NF-es');
    } finally {
      setLoading(false);
    }
  };

  // Sincronizar NFes do Omie
  const handleSincronizarOmie = async () => {
    setSincronizando(true);
    try {
      // Primeiro sincronizar NFes
      const res = await axios.post(`${API}/omie/nfes/sincronizar`);
      
      // Depois sincronizar status dos pedidos
      const resPedidos = await axios.post(`${API}/omie/pedidos/sincronizar-status`);
      
      if (res.data.success) {
        let msg = `NFes: ${res.data.importadas} importadas, ${res.data.atualizadas} atualizadas`;
        if (resPedidos.data.success && resPedidos.data.pedidos_atualizados > 0) {
          msg += ` | Pedidos: ${resPedidos.data.pedidos_atualizados} atualizados`;
        }
        toast.success(msg);
        fetchNfes(); // Recarregar lista
      } else {
        toast.error(res.data.error || 'Erro ao sincronizar');
      }
    } catch (error) {
      toast.error('Erro ao sincronizar com Omie');
    } finally {
      setSincronizando(false);
    }
  };

  // Verificar e remover NFes canceladas no Omie
  const handleVerificarCanceladas = async () => {
    setVerificandoCanceladas(true);
    try {
      const res = await axios.post(`${API}/omie/nfes/verificar-canceladas`);
      
      if (res.data.success) {
        if (res.data.canceladas_removidas > 0) {
          toast.success(`${res.data.canceladas_removidas} nota(s) cancelada(s) removida(s)`);
          fetchNfes(); // Recarregar lista
        } else {
          toast.success(`Verificação concluída. Nenhuma nota cancelada encontrada.`);
        }
      } else {
        toast.error(res.data.error || 'Erro ao verificar');
      }
    } catch (error) {
      toast.error('Erro ao verificar notas canceladas');
    } finally {
      setVerificandoCanceladas(false);
    }
  };

  // Remover nota cancelada manualmente pelo número
  const handleRemoverCancelada = async (nfeNumero) => {
    if (!nfeNumero) {
      const numero = window.prompt('Digite o número da NF-e cancelada para remover:');
      if (!numero) return;
      nfeNumero = numero;
    }
    
    if (!window.confirm(`Tem certeza que deseja remover a NFe ${nfeNumero} como cancelada?`)) return;
    
    try {
      const res = await axios.delete(`${API}/omie/nfes/remover-cancelada/${nfeNumero}`);
      if (res.data.success) {
        toast.success(`NFe ${nfeNumero} removida com sucesso!`);
        fetchNfes();
      } else {
        toast.error(res.data.error || 'Erro ao remover');
      }
    } catch (error) {
      toast.error('NFe não encontrada ou erro ao remover');
    }
  };

  // Baixar XML de uma NFe
  const handleDownloadXML = async (nfe) => {
    try {
      const res = await axios.get(`${API}/nfes/${nfe.id}/xml`);
      if (res.data.success && res.data.xml) {
        const blob = new Blob([res.data.xml], { type: 'application/xml' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `NFe_${nfe.numero_nfe || nfe.nfe_numero}.xml`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error(res.data.error || 'XML não disponível');
      }
    } catch (error) {
      toast.error('Erro ao baixar XML');
    }
  };

  // Baixar DANFE (PDF) de uma NFe
  const handleDownloadDANFE = async (nfe) => {
    try {
      const res = await axios.get(`${API}/nfes/${nfe.id}/danfe`);
      if (res.data.success && res.data.danfe_base64) {
        const byteCharacters = atob(res.data.danfe_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `DANFE_${nfe.numero_nfe || nfe.nfe_numero}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error(res.data.error || 'DANFE não disponível');
      }
    } catch (error) {
      toast.error('Erro ao baixar DANFE');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar mês de referência se está marcando como faturada
    if (formData.status_faturamento === 'faturada' && !formData.mes_referencia) {
      toast.error('Informe o mês de referência para faturar');
      return;
    }
    
    // Validar se há pelo menos um pagamento
    if (formData.pagamentos.length === 0) {
      toast.error('Adicione pelo menos um pagamento');
      return;
    }
    
    try {
      // Calcular valor total dos pagamentos
      const valorTotal = formData.pagamentos.reduce((sum, p) => sum + p.valor, 0);
      const quantidadeTotal = formData.pagamentos.reduce((sum, p) => sum + p.quantidade, 0);
      
      // Pegar dados do primeiro pagamento para compatibilidade
      const primeiroPagamento = formData.pagamentos[0];
      
      const payload = {
        ...formData,
        valor: valorTotal,
        quantidade: quantidadeTotal,
        forma: primeiroPagamento.forma,
        bandeira: primeiroPagamento.forma === 'PIX' || primeiroPagamento.forma === 'ESPÉCIE' ? null : primeiroPagamento.bandeira,
        nsu: primeiroPagamento.nsu || '',
        quant_nfe: formData.quant_nfe ? parseInt(formData.quant_nfe) : null,
        // Só envia mes_referencia se estiver faturada
        mes_referencia: formData.status_faturamento === 'faturada' ? formData.mes_referencia : null
      };

      if (editingNfe) {
        await axios.put(`${API}/nfe/${editingNfe.id}`, payload);
        toast.success('NF-e atualizada!');
      } else {
        await axios.post(`${API}/nfe`, payload);
        toast.success('NF-e adicionada!');
      }

      setDialogOpen(false);
      setEditingNfe(null);
      resetForm();
      fetchNfes();
    } catch (error) {
      toast.error('Erro ao salvar NF-e');
    }
  };

  const handleEdit = (nfe) => {
    setEditingNfe(nfe);
    // Criar pagamento a partir dos dados existentes da NF-e
    const pagamentoExistente = {
      forma: nfe.forma,
      bandeira: nfe.bandeira || 'VISA',
      valor: nfe.valor,
      quantidade: nfe.quantidade,
      nsu: nfe.nsu || ''
    };
    setFormData({
      data: nfe.data,
      nome: nfe.nome,
      mes_referencia: nfe.mes_referencia || getMesesReferencia()[3],
      faturado: nfe.faturado || '',
      nfe_numero: nfe.nfe_numero || '',
      quant_nfe: nfe.quant_nfe ? nfe.quant_nfe.toString() : '',
      observacao: nfe.observacao || '',
      status_faturamento: nfe.status_faturamento || 'nao_faturada',
      cpf_cnpj: nfe.cpf_cnpj || '',
      pagamentos: [pagamentoExistente]
    });
    // Preencher campo de busca de cliente
    setClienteSearch(nfe.nome);
    setDialogOpen(true);
  };

  // Abrir modal de visualização detalhada
  const handleView = async (nfe) => {
    // Clonar o objeto para não modificar o original
    let nfeComCpf = { ...nfe };
    
    // Se não tem cpf_cnpj, buscar do cliente pelo nome
    if (!nfe.cpf_cnpj && nfe.nome) {
      const cliente = customers.find(c => c.nome === nfe.nome);
      if (cliente && cliente.cpf_cnpj) {
        nfeComCpf.cpf_cnpj = cliente.cpf_cnpj;
      }
    }
    
    setViewingNfe(nfeComCpf);
    setOrderPayments([]);
    setViewDialogOpen(true);
    
    // Se a NF-e tem um pedido associado, buscar os pagamentos do pedido
    if (nfe.pedido_id) {
      setLoadingPayments(true);
      try {
        const res = await axios.get(`${API}/orders/${nfe.pedido_id}`);
        if (res.data && res.data.pagamentos) {
          setOrderPayments(res.data.pagamentos);
        }
      } catch (error) {
        console.error('Erro ao buscar pagamentos do pedido:', error);
      } finally {
        setLoadingPayments(false);
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta NF-e?')) return;
    try {
      await axios.delete(`${API}/nfe/${id}`);
      toast.success('NF-e excluída!');
      fetchNfes();
    } catch (error) {
      toast.error('Erro ao excluir NF-e');
    }
  };

  // Abrir modal de faturamento
  const handleOpenFaturamento = (nfe) => {
    if (nfe.status_faturamento === 'faturada') {
      // Desmarcar - não precisa de modal
      handleDesfaturar(nfe);
    } else {
      // Abrir modal para faturar
      const mesAtual = getMesesReferencia()[3]; // Mês atual
      setNfeParaFaturar(nfe);
      setFaturamentoData({
        mes_referencia: mesAtual,
        nfe_numero: nfe.nfe_numero || ''
      });
      setNfePdfFile(null); // Resetar arquivo PDF
      setFaturamentoDialogOpen(true);
    }
  };

  // Desmarcar faturamento
  const handleDesfaturar = async (nfe) => {
    try {
      await axios.put(`${API}/nfe/${nfe.id}`, {
        ...nfe,
        status_faturamento: 'nao_faturada',
        mes_referencia: null
      });
      toast.success('NF-e desmarcada!');
      fetchNfes();
    } catch (error) {
      toast.error('Erro ao desmarcar');
    }
  };

  // Confirmar faturamento com número da NF-e
  const handleConfirmarFaturamento = async () => {
    if (!faturamentoData.nfe_numero.trim()) {
      toast.error('Informe o número da NF-e');
      return;
    }
    if (!faturamentoData.mes_referencia) {
      toast.error('Selecione o mês de referência');
      return;
    }
    
    try {
      setUploadingPdf(true);
      
      // Primeiro atualizar o status da NF-e
      await axios.put(`${API}/nfe/${nfeParaFaturar.id}`, {
        ...nfeParaFaturar,
        status_faturamento: 'faturada',
        mes_referencia: faturamentoData.mes_referencia,
        nfe_numero: faturamentoData.nfe_numero
      });
      
      // Se há arquivo PDF selecionado, fazer upload
      if (nfePdfFile) {
        const formData = new FormData();
        formData.append('file', nfePdfFile);
        
        await axios.post(`${API}/nfe/${nfeParaFaturar.id}/upload-pdf`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('NF-e faturada e PDF anexado!');
      } else {
        toast.success('NF-e marcada como faturada!');
      }
      
      setFaturamentoDialogOpen(false);
      setNfeParaFaturar(null);
      setNfePdfFile(null);
      fetchNfes();
    } catch (error) {
      toast.error('Erro ao faturar');
    } finally {
      setUploadingPdf(false);
    }
  };

  // Selecionar cliente existente
  const handleSelectCliente = (customer) => {
    setFormData({ 
      ...formData, 
      nome: customer.nome,
      cpf_cnpj: customer.cpf_cnpj || customer.cpf || customer.cnpj || ''
    });
    setClienteSearch(customer.nome);
    setShowClienteDropdown(false);
  };

  // Criar novo cliente
  const handleCreateCliente = async () => {
    if (!novoCliente.nome) {
      toast.error('Informe o nome do cliente');
      return;
    }
    try {
      // Enviar cpf como cpf_cnpj para o backend
      const payload = {
        nome: novoCliente.nome,
        telefone: novoCliente.telefone,
        cpf_cnpj: novoCliente.cpf,
        tipo: novoCliente.tipo
      };
      const res = await axios.post(`${API}/customers`, payload);
      toast.success('Cliente salvo com sucesso!');
      setFormData({ ...formData, nome: res.data.nome, cpf_cnpj: novoCliente.cpf });
      setClienteSearch(res.data.nome);
      setClienteMode('existente');
      setNovoCliente({ nome: '', telefone: '', cpf: '', tipo: 'PF' });
      fetchCustomers();
    } catch (error) {
      toast.error('Erro ao salvar cliente');
    }
  };

  const resetForm = () => {
    setFormData({
      data: new Date().toISOString().split('T')[0],
      nome: '',
      mes_referencia: getMesesReferencia()[3],
      faturado: '',
      nfe_numero: '',
      quant_nfe: '',
      observacao: '',
      status_faturamento: 'nao_faturada',
      cpf_cnpj: '',
      pagamentos: []
    });
    setPagamentoAtual({
      forma: 'CRÉDITO',
      bandeira: 'VISA',
      valor: '',
      quantidade: '1',
      nsu: ''
    });
    setClienteSearch('');
    setClienteMode('existente');
    setNovoCliente({ nome: '', telefone: '', cpf: '', tipo: 'PF' });
  };
  
  // Adicionar pagamento à lista
  const handleAddPagamento = () => {
    if (!pagamentoAtual.valor || parseFloat(pagamentoAtual.valor) <= 0) {
      toast.error('Informe um valor válido');
      return;
    }
    if ((pagamentoAtual.forma === 'CRÉDITO' || pagamentoAtual.forma === 'DÉBITO') && !pagamentoAtual.bandeira) {
      toast.error('Selecione a bandeira do cartão');
      return;
    }
    
    const novoPagamento = {
      forma: pagamentoAtual.forma,
      bandeira: pagamentoAtual.forma === 'PIX' || pagamentoAtual.forma === 'ESPÉCIE' ? null : pagamentoAtual.bandeira,
      valor: parseFloat(pagamentoAtual.valor),
      quantidade: parseInt(pagamentoAtual.quantidade) || 1,
      nsu: pagamentoAtual.nsu || ''
    };
    
    setFormData({
      ...formData,
      pagamentos: [...formData.pagamentos, novoPagamento]
    });
    
    // Limpar formulário de pagamento
    setPagamentoAtual({
      forma: 'CRÉDITO',
      bandeira: 'VISA',
      valor: '',
      quantidade: '1',
      nsu: ''
    });
  };
  
  // Remover pagamento da lista
  const handleRemovePagamento = (index) => {
    setFormData({
      ...formData,
      pagamentos: formData.pagamentos.filter((_, i) => i !== index)
    });
  };

  const clearFilters = () => {
    setFilterMes('');
    setFilterForma('');
    setFilterStatus('');
  };

  const getBandeiraColor = (bandeira) => {
    const colors = {
      'VISA': 'bg-blue-500/20 text-blue-500',
      'MASTERCARD': 'bg-orange-500/20 text-orange-500',
      'ELO': 'bg-yellow-500/20 text-yellow-500',
      'AMEX': 'bg-purple-500/20 text-purple-500',
      'PIX': 'bg-green-500/20 text-green-500'
    };
    return colors[bandeira] || 'bg-gray-500/20 text-gray-500';
  };

  const getFormaColor = (forma) => {
    const colors = {
      'CRÉDITO': 'bg-purple-500/20 text-purple-500',
      'DÉBITO': 'bg-blue-500/20 text-blue-500',
      'PIX': 'bg-green-500/20 text-green-500',
      'ESPÉCIE': 'bg-yellow-500/20 text-yellow-500'
    };
    return colors[forma] || 'bg-gray-500/20 text-gray-500';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-manrope flex items-center gap-3">
              <FileText className="h-8 w-8" />
              NF-e - Notas Fiscais
            </h1>
            <p className="text-muted-foreground mt-1">Acompanhamento de faturamento fiscal</p>
          </div>

          <div className="flex gap-2">
            {/* Botão Sincronizar Omie */}
            <Button 
              variant="outline" 
              onClick={handleSincronizarOmie}
              disabled={sincronizando}
              data-testid="sync-omie-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${sincronizando ? 'animate-spin' : ''}`} />
              {sincronizando ? 'Sincronizando...' : 'Sincronizar Omie'}
            </Button>

            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                setEditingNfe(null);
                resetForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-nfe-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nova NF-e
                </Button>
              </DialogTrigger>
            <DialogContent className="w-full max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden pb-6 mb-6 sm:mb-0 rounded-2xl sm:rounded-lg">
              <DialogHeader>
                <DialogTitle>{editingNfe ? 'Editar NF-e' : 'Nova NF-e'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="data">Data</Label>
                    <Input
                      id="data"
                      type="date"
                      value={formData.data}
                      onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status_faturamento">Status</Label>
                    <Select
                      value={formData.status_faturamento}
                      onValueChange={(value) => setFormData({ ...formData, status_faturamento: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nao_faturada">Não Faturada</SelectItem>
                        <SelectItem value="faturada">Faturada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Mês de Referência - aparece apenas quando status é faturada */}
                {formData.status_faturamento === 'faturada' && (
                  <div className="space-y-2 p-3 border rounded-lg bg-green-500/5 border-green-500/30">
                    <Label htmlFor="mes_referencia" className="text-green-600 font-medium">
                      Mês de Referência do Faturamento *
                    </Label>
                    <Select
                      value={formData.mes_referencia}
                      onValueChange={(value) => setFormData({ ...formData, mes_referencia: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o mês" />
                      </SelectTrigger>
                      <SelectContent>
                        {getMesesReferencia().map(mes => (
                          <SelectItem key={mes} value={mes}>{mes}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Cliente</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={clienteMode === 'existente' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setClienteMode('existente')}
                      >
                        Existente
                      </Button>
                      <Button
                        type="button"
                        variant={clienteMode === 'novo' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setClienteMode('novo')}
                      >
                        Novo
                      </Button>
                    </div>
                  </div>
                  
                  {clienteMode === 'existente' ? (
                    <div className="relative">
                      <Input
                        placeholder="Buscar cliente..."
                        value={clienteSearch}
                        onChange={(e) => {
                          setClienteSearch(e.target.value);
                          setFormData({ ...formData, nome: e.target.value });
                          setShowClienteDropdown(true);
                        }}
                        onFocus={() => setShowClienteDropdown(true)}
                        required
                      />
                      {showClienteDropdown && clienteSearch && (
                        <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-auto">
                          {filteredCustomers.map(customer => (
                              <div
                                key={customer.id}
                                className="p-2 hover:bg-muted cursor-pointer"
                                onClick={() => handleSelectCliente(customer)}
                              >
                                <p className="font-medium">{customer.nome}</p>
                                {customer.telefone && (
                                  <p className="text-xs text-muted-foreground">{customer.telefone}</p>
                                )}
                              </div>
                            ))}
                          {filteredCustomers.length === 0 && (
                            <div className="p-2 text-muted-foreground text-sm">
                              Nenhum cliente encontrado
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
                      <Input
                        placeholder="Nome do cliente *"
                        value={novoCliente.nome}
                        onChange={(e) => setNovoCliente({ ...novoCliente, nome: e.target.value })}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Telefone"
                          value={novoCliente.telefone}
                          onChange={(e) => setNovoCliente({ ...novoCliente, telefone: e.target.value })}
                        />
                        <Input
                          placeholder="CPF/CNPJ"
                          value={novoCliente.cpf}
                          onChange={(e) => setNovoCliente({ ...novoCliente, cpf: e.target.value })}
                        />
                      </div>
                      <Button type="button" size="sm" onClick={handleCreateCliente} className="w-full">
                        <Save className="h-4 w-4 mr-1" />
                        Salvar Cliente
                      </Button>
                    </div>
                  )}
                </div>

                {/* CPF/CNPJ do cliente */}
                <div className="space-y-2">
                  <Label htmlFor="cpf_cnpj">CPF/CNPJ</Label>
                  <Input
                    id="cpf_cnpj"
                    placeholder="CPF ou CNPJ do cliente"
                    value={formData.cpf_cnpj}
                    onChange={(e) => setFormData({ ...formData, cpf_cnpj: e.target.value })}
                  />
                </div>

                {/* Seção de Pagamentos */}
                <div className="space-y-3 p-3 border rounded-lg bg-muted/20">
                  <div className="flex justify-between items-center">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Pagamentos
                    </Label>
                    <span className="text-sm font-mono text-green-500">
                      Total: R$ {formData.pagamentos.reduce((sum, p) => sum + p.valor, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>

                  {/* Lista de pagamentos adicionados */}
                  {formData.pagamentos.length > 0 && (
                    <div className="space-y-2">
                      {formData.pagamentos.map((pag, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 bg-background rounded border">
                          <div className="flex items-center gap-3">
                            <Badge className={getFormaColor(pag.forma)} variant="secondary">{pag.forma}</Badge>
                            {pag.bandeira && (
                              <Badge className={getBandeiraColor(pag.bandeira)} variant="outline">{pag.bandeira}</Badge>
                            )}
                            {pag.quantidade > 1 && (
                              <span className="text-xs text-muted-foreground">{pag.quantidade}x</span>
                            )}
                            <span className="font-bold text-green-500">R$ {pag.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            {pag.nsu && (
                              <span className="text-xs text-muted-foreground font-mono">NSU: {pag.nsu}</span>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemovePagamento(idx)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Formulário para adicionar pagamento */}
                  <div className="space-y-2 pt-2 border-t">
                    <Label className="text-xs text-muted-foreground">Adicionar Pagamento</Label>
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-2 space-y-1">
                        <Label className="text-xs">Forma</Label>
                        <Select
                          value={pagamentoAtual.forma}
                          onValueChange={(value) => setPagamentoAtual({
                            ...pagamentoAtual,
                            forma: value,
                            bandeira: value === 'PIX' || value === 'ESPÉCIE' ? '' : 'VISA'
                          })}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FORMAS_PAGAMENTO.map(forma => (
                              <SelectItem key={forma} value={forma}>{forma}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {(pagamentoAtual.forma === 'CRÉDITO' || pagamentoAtual.forma === 'DÉBITO') && (
                        <div className="col-span-2 space-y-1">
                          <Label className="text-xs">Bandeira</Label>
                          <Select
                            value={pagamentoAtual.bandeira}
                            onValueChange={(value) => setPagamentoAtual({ ...pagamentoAtual, bandeira: value })}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                            <SelectContent>
                              {BANDEIRAS.filter(b => b !== 'PIX').map(b => (
                                <SelectItem key={b} value={b}>{b}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      <div className="col-span-2 space-y-1">
                        <Label className="text-xs">Valor</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={pagamentoAtual.valor}
                          onChange={(e) => setPagamentoAtual({ ...pagamentoAtual, valor: e.target.value })}
                          className="h-9"
                        />
                      </div>
                      
                      <div className="col-span-2 space-y-1">
                        <Label className="text-xs">Parcelas</Label>
                        <Input
                          type="number"
                          min="1"
                          value={pagamentoAtual.quantidade}
                          onChange={(e) => setPagamentoAtual({ ...pagamentoAtual, quantidade: e.target.value })}
                          className="h-9"
                        />
                      </div>
                      
                      {(pagamentoAtual.forma === 'CRÉDITO' || pagamentoAtual.forma === 'DÉBITO') && (
                        <div className="col-span-2 space-y-1">
                          <Label className="text-xs">NSU</Label>
                          <Input
                            placeholder="NSU"
                            value={pagamentoAtual.nsu}
                            onChange={(e) => setPagamentoAtual({ ...pagamentoAtual, nsu: e.target.value })}
                            className="h-9"
                          />
                        </div>
                      )}
                      
                      <div className={`${pagamentoAtual.forma === 'CRÉDITO' || pagamentoAtual.forma === 'DÉBITO' ? 'col-span-2' : 'col-span-4'}`}>
                        <Button 
                          type="button" 
                          size="sm" 
                          onClick={handleAddPagamento}
                          className="w-full h-9"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Adicionar
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="quant_nfe">Quant. NF-e</Label>
                    <Input
                      id="quant_nfe"
                      type="number"
                      placeholder="Opcional"
                      value={formData.quant_nfe}
                      onChange={(e) => setFormData({ ...formData, quant_nfe: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="faturado">Nº Faturado</Label>
                    <Input
                      id="faturado"
                      placeholder="Número da fatura"
                      value={formData.faturado}
                      onChange={(e) => setFormData({ ...formData, faturado: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nfe_numero">Nº NF-e</Label>
                    <Input
                      id="nfe_numero"
                      placeholder="Número da NF-e"
                      value={formData.nfe_numero}
                      onChange={(e) => setFormData({ ...formData, nfe_numero: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="observacao">Observação</Label>
                  <Input
                    id="observacao"
                    placeholder="Observação (opcional)"
                    value={formData.observacao}
                    onChange={(e) => setFormData({ ...formData, observacao: e.target.value })}
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1">
                    {editingNfe ? 'Salvar' : 'Adicionar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Card className="border-border border-green-500/50 bg-green-500/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-green-600 flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  Total Faturado
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono text-green-500" data-testid="total-faturado">
                  R$ {(stats.total_faturado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.qtd_faturadas || 0} registros
                </p>
              </CardContent>
            </Card>

            <Card className="border-border border-blue-500/50 bg-blue-500/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-blue-600 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Faturamento {new Date().toLocaleDateString('pt-BR', { month: 'long' }).charAt(0).toUpperCase() + new Date().toLocaleDateString('pt-BR', { month: 'long' }).slice(1)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono text-blue-500" data-testid="faturamento-mes">
                  R$ {(stats.faturamento_mes_atual || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.qtd_mes_atual || 0} NF-es no mês
                </p>
              </CardContent>
            </Card>

            <Card className="border-border border-yellow-500/50 bg-yellow-500/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-yellow-500 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Não Faturadas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono text-yellow-500" data-testid="total-nao-faturadas">
                  R$ {(stats.total_nao_faturado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.qtd_nao_faturadas || 0} registros
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Faturamento Anual */}
        {Object.keys(faturamentoAnual).length > 0 && (
          <Card className="border-border border-blue-500/30 bg-blue-500/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-blue-600 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Faturamento Anual (NF-es Faturadas)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(faturamentoAnual)
                  .sort(([a], [b]) => parseInt(b) - parseInt(a))
                  .map(([ano, dados]) => (
                    <div key={ano} className="p-3 bg-background rounded-lg border">
                      <p className="text-sm text-muted-foreground font-medium">Ano {ano}</p>
                      <p className="text-xl font-bold font-mono text-blue-600">
                        R$ {dados.total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-muted-foreground">{dados.quantidade} NF-es</p>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Gráfico de Linhas: Faturamento Mensal (últimos 12 meses) */}
        {faturamentoMensal.length > 0 && (
          <Card className="border-border" data-testid="nfe-chart-mensal">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                Faturamento Mensal (últimos 12 meses)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full h-72" data-testid="nfe-chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={faturamentoMensal} margin={{ top: 8, right: 16, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} />
                    <XAxis dataKey="label" stroke="currentColor" fontSize={12} />
                    <YAxis
                      stroke="currentColor" fontSize={11}
                      tickFormatter={(v) => `R$ ${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k`}
                    />
                    <RTooltip
                      contentStyle={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
                      formatter={(value, name) => {
                        if (name === 'valor') return [`R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Faturado'];
                        return [value, name];
                      }}
                      labelFormatter={(label, payload) => {
                        const p = payload?.[0]?.payload;
                        return p ? `${label} — ${p.quantidade || 0} NF-e${(p.quantidade || 0) === 1 ? '' : 's'}` : label;
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="valor" name="Faturado" stroke="hsl(var(--primary))" strokeWidth={2.5}
                          dot={{ r: 3, fill: 'hsl(var(--primary))' }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="border-border">
          <CardContent className="pt-4 space-y-3">
            {/* Campo de busca por nome ou número da NF-e */}
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar por nome do cliente ou número da NF-e..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-9"
                data-testid="nfe-search-input"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                  aria-label="Limpar busca"
                  data-testid="nfe-search-clear"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filtros:</span>
              </div>
              
              <Select value={filterMes} onValueChange={setFilterMes}>
                <SelectTrigger className="w-auto min-w-[150px]">
                  <SelectValue placeholder="Mês Ref." />
                </SelectTrigger>
                <SelectContent>
                  {ordenarMesesCronologicamente(stats?.meses_disponiveis || getMesesReferencia()).map(mes => (
                    <SelectItem key={mes} value={mes}>{mes}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterForma} onValueChange={setFilterForma}>
                <SelectTrigger className="w-auto min-w-[130px]">
                  <SelectValue placeholder="Forma Pgto" />
                </SelectTrigger>
                <SelectContent>
                  {FORMAS_PAGAMENTO.map(forma => (
                    <SelectItem key={forma} value={forma}>{forma}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-auto min-w-[120px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nao_faturada">Não Faturada</SelectItem>
                  <SelectItem value="faturada">Faturada</SelectItem>
                </SelectContent>
              </Select>

              {(filterMes || filterForma || filterStatus || searchTerm) && (
                <Button variant="ghost" size="sm" onClick={() => { clearFilters(); setSearchTerm(''); }}>
                  <X className="h-4 w-4 mr-1" />
                  Limpar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Abas de Faturamento */}
        <div className="flex gap-2">
          <Button
            variant={filterFaturamento === 'todas' ? 'default' : 'outline'}
            onClick={() => setFilterFaturamento('todas')}
            className="gap-2"
          >
            Todas
            <Badge variant="secondary" className="ml-1">
              {nfes.length}
            </Badge>
          </Button>
          <Button
            variant={filterFaturamento === 'nao_faturadas' ? 'default' : 'outline'}
            onClick={() => setFilterFaturamento('nao_faturadas')}
            className="gap-2 border-yellow-500/50 hover:border-yellow-500"
          >
            <span className="h-2 w-2 rounded-full bg-yellow-500"></span>
            Não Faturadas
            <Badge variant="secondary" className="ml-1 bg-yellow-500/20 text-yellow-500">
              {nfes.filter(n => n.status_faturamento !== 'faturada').length}
            </Badge>
          </Button>
          <Button
            variant={filterFaturamento === 'faturadas' ? 'default' : 'outline'}
            onClick={() => setFilterFaturamento('faturadas')}
            className="gap-2 border-green-500/50 hover:border-green-500"
          >
            <span className="h-2 w-2 rounded-full bg-green-500"></span>
            Faturadas
            <Badge variant="secondary" className="ml-1 bg-green-500/20 text-green-500">
              {nfes.filter(n => n.status_faturamento === 'faturada').length}
            </Badge>
          </Button>
        </div>

        {/* Table */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Mobile: Cards */}
            <div className="md:hidden divide-y divide-border">
              {paginatedNfes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhuma NF-e encontrada
                </div>
              ) : (
                paginatedNfes.map((nfe) => (
                  <div key={nfe.id} className="p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium">{nfe.nome}</p>
                        <p className="text-xs text-muted-foreground">{formatDateDisplay(nfe.data)} • {nfe.mes_referencia || '-'}</p>
                      </div>
                      <Badge className={nfe.status_faturamento === 'faturada' ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'} variant="secondary">
                        {nfe.status_faturamento === 'faturada' ? 'Faturada' : 'Pendente'}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-mono font-bold">R$ {nfe.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        {nfe.nfe_numero && <span className="ml-2 text-xs text-blue-500">NF-e #{nfe.nfe_numero}</span>}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setViewingNfe(nfe); setViewDialogOpen(true); }}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingNfe(nfe); setDialogOpen(true); }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {nfe.origem_omie && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleRemoverCancelada(nfe.nfe_numero)} title="Remover cancelada">
                            <XCircle className="h-4 w-4 text-orange-500" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(nfe.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {/* Desktop: Table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Pedido</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead>Mês Ref.</TableHead>
                    <TableHead>Nº NF-e</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedNfes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        Nenhuma NF-e encontrada
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedNfes.map((nfe) => (
                      <TableRow key={nfe.id}>
                        <TableCell>
                          <Badge 
                            className={nfe.status_faturamento === 'faturada' 
                              ? 'bg-green-500/20 text-green-500' 
                              : 'bg-yellow-500/20 text-yellow-500'
                            }
                            variant="secondary"
                          >
                            {nfe.status_faturamento === 'faturada' ? 'Faturada' : 'Pendente'}
                          </Badge>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatDateDisplay(nfe.data)}
                        </TableCell>
                        <TableCell className="font-medium">{nfe.nome}</TableCell>
                        <TableCell>
                          {nfe.pedido_numero ? (
                            <span className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">
                              {nfe.pedido_numero}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-green-500 font-bold whitespace-nowrap">
                          R$ {nfe.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{nfe.mes_referencia}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{nfe.nfe_numero || '-'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant={nfe.status_faturamento === 'faturada' ? 'default' : 'outline'}
                              size="icon"
                              onClick={() => handleOpenFaturamento(nfe)}
                              title={nfe.status_faturamento === 'faturada' ? 'Desmarcar faturada' : 'Faturar NF-e'}
                              className={nfe.status_faturamento === 'faturada' ? 'bg-green-600 hover:bg-green-700' : ''}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleView(nfe)}
                              title="Ver detalhes"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {/* Botões de download - apenas para NFes sincronizadas com Omie */}
                            {nfe.chave_nfe && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDownloadXML(nfe)}
                                  title="Baixar XML"
                                >
                                  <FileDown className="h-4 w-4 text-blue-500" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDownloadDANFE(nfe)}
                                  title="Baixar DANFE (PDF)"
                                >
                                  <Download className="h-4 w-4 text-green-500" />
                                </Button>
                              </>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(nfe)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {nfe.origem_omie && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoverCancelada(nfe.nfe_numero)}
                                title="Remover como nota cancelada"
                              >
                                <XCircle className="h-4 w-4 text-orange-500" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(nfe.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            
            {/* Paginação */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <div className="text-sm text-muted-foreground">
                  Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, filteredNfes.length)} de {filteredNfes.length} registros
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    Primeira
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className="w-8 h-8 p-0"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Próxima
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Última
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabela de Faturamento por Mês */}
        {stats && stats.por_mes && Object.keys(stats.por_mes).length > 0 && (
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Total Faturado por Mês
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mês de Referência</TableHead>
                      <TableHead className="text-right">Valor Faturado</TableHead>
                      <TableHead className="text-right">Quantidade</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(stats.por_mes)
                      .sort((a, b) => {
                        // Ordenar por mês/ano (mais recente primeiro)
                        const [mesA, anoA] = a[0].split('/');
                        const [mesB, anoB] = b[0].split('/');
                        if (anoA !== anoB) return parseInt(anoB) - parseInt(anoA);
                        const meses = ['JANEIRO', 'FEVEREIRO', 'MARÇO', 'ABRIL', 'MAIO', 'JUNHO', 
                                       'JULHO', 'AGOSTO', 'SETEMBRO', 'OUTUBRO', 'NOVEMBRO', 'DEZEMBRO'];
                        return meses.indexOf(mesB) - meses.indexOf(mesA);
                      })
                      .map(([mes, data]) => (
                        <TableRow key={mes}>
                          <TableCell>
                            <Badge variant="outline" className="font-medium">
                              {mes}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono text-green-500">
                            R$ {data.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {data.quantidade}
                          </TableCell>
                        </TableRow>
                      ))}
                    {/* Linha de total */}
                    <TableRow className="bg-muted/50 font-bold">
                      <TableCell>TOTAL</TableCell>
                      <TableCell className="text-right font-mono text-green-500">
                        R$ {(stats.total_faturado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {stats.qtd_faturadas || 0}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Modal de Faturamento */}
        <Dialog open={faturamentoDialogOpen} onOpenChange={setFaturamentoDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-6 mb-6 sm:mb-0 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                Faturar NF-e
              </DialogTitle>
            </DialogHeader>
            {nfeParaFaturar && (
              <div className="space-y-4">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Cliente</p>
                  <p className="font-medium">{nfeParaFaturar.nome}</p>
                  <p className="text-sm text-muted-foreground mt-2">Valor</p>
                  <p className="font-mono text-lg text-green-500">
                    R$ {nfeParaFaturar.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="nfe_numero_faturar" className="text-red-500">
                    Número da NF-e *
                  </Label>
                  <Input
                    id="nfe_numero_faturar"
                    placeholder="Ex: 001234"
                    value={faturamentoData.nfe_numero}
                    onChange={(e) => setFaturamentoData({ ...faturamentoData, nfe_numero: e.target.value })}
                    required
                    autoFocus
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="mes_referencia_faturar">Mês de Referência</Label>
                  <Select
                    value={faturamentoData.mes_referencia}
                    onValueChange={(value) => setFaturamentoData({ ...faturamentoData, mes_referencia: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o mês" />
                    </SelectTrigger>
                    <SelectContent>
                      {getMesesReferencia().map(mes => (
                        <SelectItem key={mes} value={mes}>{mes}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Upload do arquivo PDF da NF-e */}
                <div className="space-y-2">
                  <Label htmlFor="nfe_pdf_upload" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Anexar PDF da NF-e (Opcional)
                  </Label>
                  <Input
                    id="nfe_pdf_upload"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setNfePdfFile(e.target.files?.[0] || null)}
                    className="cursor-pointer"
                  />
                  {nfePdfFile && (
                    <p className="text-xs text-green-500 flex items-center gap-1">
                      <Check className="h-3 w-3" />
                      Arquivo selecionado: {nfePdfFile.name}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    O arquivo PDF será vinculado à NF-e e também ao pedido correspondente.
                  </p>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1" 
                    onClick={() => setFaturamentoDialogOpen(false)}
                    disabled={uploadingPdf}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="button" 
                    className="flex-1 bg-green-600 hover:bg-green-700" 
                    onClick={handleConfirmarFaturamento}
                    disabled={uploadingPdf}
                  >
                    {uploadingPdf ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Confirmar Faturamento
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal de Visualização Detalhada */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden pb-6 mb-6 sm:mb-0 p-3 sm:p-6 gap-2 rounded-2xl sm:rounded-lg">
            <DialogHeader className="pb-0">
              <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
                <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
                Detalhes da NF-e
              </DialogTitle>
            </DialogHeader>
            {viewingNfe && (
              <div className="space-y-3">
                {/* Informações do Cliente */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-2 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground">Cliente</p>
                    <p className="font-semibold text-sm">{viewingNfe.nome}</p>
                    {viewingNfe.cpf_cnpj && (
                      <p className="text-xs text-muted-foreground">CPF/CNPJ: {viewingNfe.cpf_cnpj}</p>
                    )}
                  </div>
                  <div className="sm:text-right">
                    <p className="text-xs text-muted-foreground">Pedido</p>
                    <p className="font-mono text-sm">
                      {viewingNfe.pedido_numero || viewingNfe.pedido_id?.slice(0, 8) || '-'}
                    </p>
                  </div>
                </div>

                {/* Tabela de Pagamentos do Pedido */}
                <div>
                  <h4 className="font-semibold mb-1 flex items-center gap-2 text-sm">
                    <CreditCard className="h-4 w-4" />
                    Pagamentos {viewingNfe.pedido_id ? 'do Pedido' : 'da NF-e'}
                  </h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full caption-bottom text-sm">
                      <thead className="[&_tr]:border-b">
                        <tr className="bg-muted/50 border-b">
                          <th className="h-7 px-2 text-left align-middle font-medium text-muted-foreground text-xs">Data</th>
                          <th className="h-7 px-2 text-left align-middle font-medium text-muted-foreground text-xs">Forma</th>
                          <th className="h-7 px-2 text-right align-middle font-medium text-muted-foreground text-xs">Valor</th>
                          <th className="h-7 px-2 text-left align-middle font-medium text-muted-foreground text-xs">NSU</th>
                        </tr>
                      </thead>
                      <tbody className="[&_tr:last-child]:border-0">
                        {loadingPayments ? (
                          <tr>
                            <td colSpan={4} className="p-3 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                                <span className="text-muted-foreground text-xs">Carregando...</span>
                              </div>
                            </td>
                          </tr>
                        ) : orderPayments.length > 0 ? (
                          <>
                            {orderPayments.map((pag, idx) => (
                              <tr key={pag.id || idx} className="border-b transition-colors hover:bg-muted/50">
                                <td className="p-1.5 align-middle font-mono text-xs whitespace-nowrap">
                                  {pag.data_pagamento 
                                    ? formatDateDisplay(pag.data_pagamento)
                                    : pag.data 
                                      ? formatDateDisplay(pag.data) 
                                      : '-'}
                                </td>
                                <td className="p-1.5 align-middle">
                                  <Badge className={`text-xs ${getFormaColor(pag.forma_pagamento?.toUpperCase())}`} variant="secondary">
                                    {pag.forma_pagamento || '-'}
                                  </Badge>
                                  {pag.bandeira && (
                                    <span className="ml-1 text-xs text-muted-foreground">{pag.bandeira}</span>
                                  )}
                                  {pag.parcelas > 1 && (
                                    <span className="ml-1 text-xs font-mono">{pag.parcelas}x</span>
                                  )}
                                </td>
                                <td className="p-1.5 align-middle text-right font-mono font-bold text-green-500 text-xs whitespace-nowrap">
                                  R$ {(pag.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="p-1.5 align-middle font-mono text-xs text-muted-foreground">
                                  {pag.nsu || '-'}
                                </td>
                              </tr>
                            ))}
                            {/* Linha de Total */}
                            <tr className="bg-muted/30 font-bold border-t-2">
                              <td colSpan={2} className="p-1.5 align-middle text-right text-xs">
                                Total:
                              </td>
                              <td className="p-1.5 align-middle text-right font-mono text-green-500 text-sm whitespace-nowrap">
                                R$ {orderPayments.reduce((sum, p) => sum + (p.valor || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td></td>
                            </tr>
                          </>
                        ) : (
                          /* Fallback: mostrar dados da própria NF-e se não houver pedido */
                          <tr className="border-b transition-colors hover:bg-muted/50">
                            <td className="p-1.5 align-middle font-mono text-xs whitespace-nowrap">
                              {formatDateDisplay(viewingNfe.data)}
                            </td>
                            <td className="p-1.5 align-middle">
                              <Badge className={`text-xs ${getFormaColor(viewingNfe.forma)}`} variant="secondary">
                                {viewingNfe.forma}
                              </Badge>
                              {viewingNfe.bandeira && (
                                <span className="ml-1 text-xs text-muted-foreground">{viewingNfe.bandeira}</span>
                              )}
                              {viewingNfe.quantidade > 1 && (
                                <span className="ml-1 text-xs font-mono">{viewingNfe.quantidade}x</span>
                              )}
                            </td>
                            <td className="p-1.5 align-middle text-right font-mono font-bold text-green-500 text-sm whitespace-nowrap">
                              R$ {viewingNfe.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="p-1.5 align-middle font-mono text-xs text-muted-foreground">
                              {viewingNfe.nsu || '-'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Status de Faturamento */}
                <div className="grid grid-cols-3 gap-1.5">
                  <div className="p-1.5 border rounded-lg">
                    <p className="text-xs text-muted-foreground">Status</p>
                    <Badge 
                      className={`text-xs ${viewingNfe.status_faturamento === 'faturada' 
                        ? 'bg-green-500/20 text-green-500' 
                        : 'bg-yellow-500/20 text-yellow-500'
                      }`}
                    >
                      {viewingNfe.status_faturamento === 'faturada' ? 'Faturada' : 'Pendente'}
                    </Badge>
                  </div>
                  <div className="p-1.5 border rounded-lg">
                    <p className="text-xs text-muted-foreground">Mês Ref.</p>
                    <p className="font-semibold text-sm">{viewingNfe.mes_referencia || '-'}</p>
                  </div>
                  <div className="p-1.5 border rounded-lg">
                    <p className="text-xs text-muted-foreground">NF-e Nº</p>
                    <p className="font-mono font-semibold text-sm">{viewingNfe.nfe_numero || '-'}</p>
                  </div>
                </div>

                {/* Observação */}
                {viewingNfe.observacao && (
                  <div className="p-1.5 border rounded-lg">
                    <p className="text-xs text-muted-foreground">Observação</p>
                    <p className="text-xs">{viewingNfe.observacao}</p>
                  </div>
                )}

                {/* Botões */}
                <div className="flex gap-2 pt-1">
                  <Button 
                    variant="outline" 
                    className="flex-1 h-9 text-sm" 
                    onClick={() => setViewDialogOpen(false)}
                  >
                    Fechar
                  </Button>
                  <Button 
                    variant="default" 
                    className="flex-1 h-9 text-sm" 
                    onClick={() => {
                      setViewDialogOpen(false);
                      handleEdit(viewingNfe);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Editar
                  </Button>
                  {viewingNfe.status_faturamento !== 'faturada' && (
                    <Button 
                      className="flex-1 h-9 bg-green-600 hover:bg-green-700 text-sm" 
                      onClick={() => {
                        setViewDialogOpen(false);
                        handleOpenFaturamento(viewingNfe);
                      }}
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Faturar
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
